import './styles/ProductOverview.scss';

import { Paper, Tab, Tabs } from '@mui/material';
import { Product, ProductType } from '@thro-types/leihbar';

import { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useProductQuery } from 'api/hooks/leihbar';
import { useSelfEntitlementGroupsQuery } from 'api/hooks/users';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import usePageHeading from 'utils/hooks/usePageHeading';
import useQueryStringState from 'utils/hooks/useQueryStringState';

import { ArticlesOverview } from '../../Articles/ArticlesOverview';
import { canUserEdit, canUserView, isProductABundle } from '../../helper';
import { ProductBookingHistory } from '../ProductBookingHistory';
import { ProductDetailPage } from '../ProductDetailPage';
import { ProductDetailSkeleton } from '../ProductDetailPage/components/ProductDetailSkeleton';

enum TabPanels {
  PRODUCT = 'product',
  ARTICLES = 'articles',
  BOOKINGS = 'bookings'
}

export const ProductOverview = () => {
  const { permissions, user } = useAuthContext();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [tab, setTab] = useQueryStringState<TabPanels>('tab', TabPanels.PRODUCT);

  const handleTabChange = useCallback(
    (_event, value) => {
      setTab(value);
    },
    [setTab]
  );

  const { data: product, isLoading } = useProductQuery(parseInt(id!, 10));
  const { data: myEntitlementGroups } = useSelfEntitlementGroupsQuery();

  usePageHeading(
    {
      heading: isLoading ? t(translations.common.loading()) : product?.name,
      headingProps: { back: location.state?.back ?? `/leihbar` }
    },
    [isLoading]
  );

  const renderTabContent = (product: Product) => {
    switch (tab) {
      case TabPanels.PRODUCT:
        return <ProductDetailPage product={product} setTab={setTab} />;
      case TabPanels.ARTICLES:
        if (!(product.productType === ProductType.BUNDLE)) {
          return <ArticlesOverview />;
        } else {
          setTab(TabPanels.PRODUCT);
          return <></>;
        }
      case TabPanels.BOOKINGS:
        if (!(product.productType === ProductType.BUNDLE)) {
          return <ProductBookingHistory />;
        } else {
          setTab(TabPanels.PRODUCT);
          return <></>;
        }
      default:
        return <></>;
    }
  };

  if (!product) {
    return <ProductDetailSkeleton />;
  }

  const canEdit = canUserEdit(product, permissions, user, myEntitlementGroups);

  const canView = canUserView(product, permissions, user, myEntitlementGroups);

  if (!canView) {
    dispatch(AlertActions.error(t(translations.authorization.notAuthorizedRoute())));
    navigate('/leihbar');
  }

  return (
    <>
      <Helmet>
        <title>{t(translations.pages.leihbar.product.name())}</title>
      </Helmet>
      <Paper>
        {canEdit && !isProductABundle(product) && (
          <Tabs value={tab} onChange={handleTabChange} indicatorColor="primary">
            {Object.values(TabPanels).map(tab => {
              return (
                <Tab
                  value={tab.toLowerCase()}
                  key={tab}
                  label={t(translations.pages.leihbar.product.tabs[tab.toLowerCase()]())}
                />
              );
            })}
          </Tabs>
        )}
        <>{renderTabContent(product)}</>
      </Paper>
    </>
  );
};
